import React, { ReactElement } from 'react';
import { useScoreInfoTrigger } from '../../ScoreInfo/ScoreInfoModal';

interface ScoreBlockProps {
    label: string;
    icon: string;
    score?: number | null;
    unavailableReason?: string | null;
    // Direction of the scale. Health is higher-is-better; Risk is a magnitude (1 low, 10 high)
    // and passes `false` so the color bands invert with it.
    higherIsBetter?: boolean;
}

export type ScoreBand = 'good' | 'warn' | 'bad';

// Bands follow the Databricks score tiers (0-100 there, 0-10 here). Health: Healthy >= 9,
// Warning / At Risk >= 5, Critical below. Risk: Low <= 1.5, Moderate / High <= 5.5, Critical above.
// Exported because the breakdown table colours its score badges by the same bands — the
// thresholds must be decided in exactly one place or a row and its group can disagree.
export const scoreBand = (score: number, higherIsBetter: boolean): ScoreBand => {
    if (higherIsBetter) {
        if (score >= 9) return 'good';
        if (score >= 5) return 'warn';
        return 'bad';
    }

    if (score <= 1.5) return 'good';
    if (score <= 5.5) return 'warn';
    return 'bad';
};

const ScoreBlock = ({ label, icon, score, unavailableReason, higherIsBetter = true }: ScoreBlockProps): ReactElement => {
    // The locations payload rides in through a passthrough schema and a cast, so guard the
    // value rather than trusting the prop type — a non-numeric score would throw on toFixed
    // and take the whole Overview tab down with the error boundary.
    const numericScore = typeof score === 'number' && Number.isFinite(score) ? score : null;
    const hasScore = numericScore !== null;
    const { className: triggerClassName = '', ...triggerProps } = useScoreInfoTrigger({ label, score: numericScore, unavailableReason });

    return (
        <div
            {...triggerProps}
            className={`lst-score-block ${hasScore ? `lst-score-block--${scoreBand(numericScore, higherIsBetter)}` : 'lst-score-block--placeholder'} ${triggerClassName}`}
            title={hasScore ? undefined : unavailableReason ?? undefined}
        >
            <div className='lst-score-block__header'>
                <i className={`${icon} lst-score-block__icon`} />
                <span className='lst-score-block__label'>{label}</span>
            </div>
            <span className='lst-score-block__value'>{hasScore ? numericScore.toFixed(1) : '—'}</span>
        </div>
    );
};

export default ScoreBlock;
